import { ITEM_STATUS_CREATED, ITEM_STATUS_PENDING } from 'constants/cart'
import { CHANNEL_MANAGER_DERBYSOFT } from 'constants/reservation'
import moment from 'moment'

interface RefundRequestOptions {
  /**
   * Ignores whether or not insurance (and refund protect) affect the
   * availability of the call
   **/
  ignoreInsurance?: boolean
}

export function itemCanRequestRefund(item: App.OrderItem, order: App.Order, options: RefundRequestOptions = {}, cancellationConfig?: App.OrderItemHotelCancellationConfig) {
  // only support flash hotels at the moment, LPC hotels don't have the same
  // relationship at the moment to use the refund request portal
  return item.offer.type === 'hotel' &&
    // order cannot have experiences or flights - we don't support
    // the interaction between these items and a refund request isn't set yet
    order.experienceItems.length === 0 &&
    order.transferItems.length === 0 &&
    order.flightItems.length === 0 &&
    // can't already be refunded
    !order.refunds.some(refund => refund.itemId === item.id) &&
    // can't already be cancelled
    item.status !== 'cancelled' &&
    // no existing refund requests
    !order.refundRequests[item.id] &&
    // The cancellation policy allows requesting refunds
    cancellationConfig?.canRequestRefund &&
    // don't need to make a refund request if they can *actually* refund!
    !cancellationConfig.canRefund &&
    // Don't need a refund request if they can use booking or insurance booking protection
    (options.ignoreInsurance || (!cancellationConfig.canUseBookingProtection && !cancellationConfig.canUseInsuranceBookingProtection)) &&
    // Block refund if insurance claim is pending
    !order.locked
}

export function isPolicyDeadlineBeforePurchase(
  policyDeadline: string | undefined,
  order: App.Order,
  policyDeadlineInstant?: string,
): boolean {
  if (policyDeadlineInstant) {
    return moment(policyDeadlineInstant).isBefore(moment(order.createdAt))
  }
  return !!policyDeadline && moment(policyDeadline).isBefore(moment(order.createdAt), 'day')
}

export function canRefundItem(item: App.OrderItem, order: App.Order, cancellationConfig?: App.OrderItemHotelCancellationConfig) {
  // can't refund a cancelled item
  return item.status !== 'cancelled' &&
  // can't already have a refund
    !order.refunds.some(refund => refund.itemId === item.id) &&
  // can't have a refund request against it
    !order.refundRequests[item.id] &&
  // Block refund if insurance claim is pending
    !order.locked &&
    (
    // cancellation policy must allow it or they have insurance to allow it
      cancellationConfig?.canRefund ||
      cancellationConfig?.canUseBookingProtection ||
      cancellationConfig?.canUseInsuranceBookingProtection ||
      cancellationConfig?.canUseCancellationProtection
    )
}

export function isItemRefundRequestApproved(order: App.Order, item: App.OrderItem) {
  return order.refundRequests[item.id].status === 'approved'
}

export function isItemRefundRequestBeforeCheckIn(item: App.OrderItem) {
  const checkIn = moment(item.reservation?.startDate)
  return moment().isBefore(checkIn)
}

export function orderItemHasReservationChannelManager(
  item: Pick<App.OrderItem, 'reservation'> | undefined,
  channelManager: string,
) {
  return item?.reservation?.channelManager === channelManager
}

export function isDerbysoftReservation(item: Pick<App.OrderItem, 'reservation'> | undefined) {
  return orderItemHasReservationChannelManager(item, CHANNEL_MANAGER_DERBYSOFT)
}

export function canRefundCarHireItem(item: App.OrderCarHireItem, order: App.Order, cancellationPolicies?: App.CarHireCancellationPolicy) {
  if (!cancellationPolicies) return false

  // can't refund a cancelled item
  return item.status !== 'cancelled' &&
  // can't already have a refund
    !order.refunds.some(refund => refund.itemId === item.id) &&
    moment().isBefore(moment(cancellationPolicies.maxDate))
}

export function hasPendingExperienceItems(order: App.Order) {
  return order.experienceItems.some(item => item.status === ITEM_STATUS_CREATED || item.status === ITEM_STATUS_PENDING)
}
